import * as Toast from "@radix-ui/react-toast";
import { Pencil } from "lucide-react";
import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

interface RegionEditDialogProps {
  id: number;
  currentName: string;
  currentObservation: string;
  onRegionUpdate: () => void;
}

export function RegionEditDialog({
  id,
  currentName,
  currentObservation,
  onRegionUpdate,
}: RegionEditDialogProps) {
  const [openModel, setOpenModel] = useState(false);
  const [name, setName] = useState(currentName);
  const [observation, setObservation] = useState(currentObservation);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [toastMessage, setToastMessage] = useState("");
  const [loading, setLoading] = useState(false);

  function handleToOpenModel() {
    setOpenModel(true);
    setName(currentName);
    setObservation(currentObservation);
  }

  async function handleSaveChanges() {
    if (!name.trim()) {
      setToastType("error");
      setToastMessage("O nome da região é obrigatório!");
      setToastOpen(true);
      return;
    }
    setLoading(true);
    const updatedRegion = {
      data: {
        Name: name,
        Observation: observation,
      },
    };
    try {
      const route = import.meta.env.VITE_URL_ENDPOINT;
      const response = await fetch(`${route}/api/regions/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedRegion),
      });
      if (!response.ok) throw new Error("Failed to update region");
      setOpenModel(false);
      onRegionUpdate();
      setToastType("success");
      setToastMessage("Região editada com sucesso!");
      setToastOpen(true);
    } catch (error) {
      setToastType("error");
      setToastMessage("Falha ao atualizar a região!");
      setToastOpen(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Toast.Provider swipeDirection="right">
        <Dialog open={openModel} onOpenChange={setOpenModel}>
          <DialogTrigger asChild>
            <Button
              onClick={handleToOpenModel}
              className="rounded-full bg-gray-100 p-2 hover:bg-gray-200"
              title="Editar região"
            >
              <Pencil className="text-gray-700" />
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-xl bg-white p-6 shadow-xl sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                Editar Região
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Após editar basta clicar em SALVAR
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="name" className="mb-1 block">
                  Nome <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nome da região"
                  className="w-full"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="observation" className="mb-1 block">
                  Observação
                </Label>
                <Textarea
                  id="observation"
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  placeholder="Observações sobre a região"
                  className="w-full"
                  disabled={loading}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                onClick={handleSaveChanges}
                disabled={loading}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                {loading ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Toast.Root
          open={toastOpen}
          onOpenChange={setToastOpen}
          className={`fixed bottom-0 right-0 m-6 rounded-md p-4 shadow-md ${
            toastType === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <Toast.Description>{toastMessage}</Toast.Description>
          <Toast.Close className="absolute right-1 top-1" />
        </Toast.Root>
        <Toast.Viewport />
      </Toast.Provider>
    </div>
  );
}
